import useLocalStorage, { STORAGE_KEY } from '@hooks/useLocalStorage';
import pathnames from '@src/app/routes/pathnames';
import log from '@src/datadog';
import React, {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useAuth } from 'react-oidc-context';
import { useHistory } from 'react-router-dom';

interface AuthenticationContextProps {
  doLogout?: () => void;
}

const AuthenticationContext = createContext<AuthenticationContextProps>({});

const AuthenticationContextProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const auth = useAuth();
  const history = useHistory();

  const navigateLogout = () => {
    history.push(pathnames.login());
  };

  const { removeStorage } = useLocalStorage();

  const doLogout = useCallback(async () => {
    removeStorage(STORAGE_KEY.MARKETING_ID());
    await auth.removeUser();
    await auth.revokeTokens(['access_token', 'refresh_token']);
    await auth.clearStaleState();

    navigateLogout();

    log.clearDDUser();
  }, [auth, removeStorage]);

  const authenticationContextValue = useMemo(
    () => ({
      doLogout,
    }),
    [doLogout],
  );

  return (
    <AuthenticationContext.Provider value={authenticationContextValue}>
      {!auth.isLoading && children}
    </AuthenticationContext.Provider>
  );
};

const useAuthenticationContext = () => {
  return useContext(AuthenticationContext);
};

export {
  AuthenticationContext,
  AuthenticationContextProvider,
  useAuthenticationContext,
};
